import type { PlanFeature } from '#shared/organizations/planFeatures'

/**
 * The plan-gated page trees (MAR-199) — the client half of the gate. The server half, which is the
 * authoritative one, is `server/middleware/zzfeature.requireFeature.ts`; `feature-gate-tables.spec.ts`
 * (DET-PLAT-043) reads both tables as text and fails when they disagree, so keep this a flat literal.
 *
 * What a match does (MAR-980, decision D2): `pages/app.vue` renders `PlanFeatureNotice` in place of
 * the page when the org's plan lacks the feature — the address stays, the shell stays, and the page
 * itself never mounts, so it asks for nothing the plan refuses. It used to redirect to `/app`.
 */
const GATED_PREFIXES: ReadonlyArray<readonly [prefix: string, feature: PlanFeature]> = [
  // Chat (MAR-1212): a conversation and the history read `/api/conversations`, which the server gates
  // on `chat`. The chat HOME (`/app`) is not a prefix here (it would swallow every page); it is
  // handled by `blockedFeatureFor` below.
  ['/app/c', 'chat'],
  ['/app/history', 'chat'],
  // Expedientes is one nav entry over two sibling routes (see IconRail.vue's `match`) — clients
  // ride the same gate as cases.
  ['/app/cases', 'case_management'],
  ['/app/clients', 'case_management'],
  ['/app/sheets', 'sheets'],
  ['/app/workflows', 'workflows'],
  ['/app/runs', 'workflows'],
  // Authoring (MAR-1186): the template library, which feeds the workflow builder. Plan Redacción runs
  // workflows and has no `workflow_builder`, so these show the plan notice.
  ['/app/settings/templates', 'workflow_builder'],
  ['/app/settings/document-templates', 'workflow_builder'],
  ['/app/contract-review', 'contract_review'],
]

/** The feature a page path is gated on, or `null` for a path every plan reaches. */
export function gatedFeatureFor(path: string): PlanFeature | null {
  const match = GATED_PREFIXES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))
  return match ? match[1] : null
}

/**
 * The `/app` pages a member WITHOUT a seat can still use (MAR-1212 post-merge F3): getting into an
 * organization, the superadmin console, their own account, and organization administration (settings,
 * members, roles, and the seat screen where an admin assigns a license, also to themselves). Each reads only routes the server's `app` gate exempts
 * (`zzfeature.requireFeature.ts`). Every other `/app` page shows the plan notice for `app`.
 */
const SEAT_FREE_PREFIXES = [
  '/app/setup',
  '/app/admin',
  '/app/settings/profile',
  '/app/settings/password',
  '/app/settings/security',
  '/app/settings/language',
  '/app/settings/appearance',
  '/app/settings/privacy',
  '/app/settings/logout',
  // The organization's own settings, members and roles: administration a founder does before buying.
  '/app/settings/organization',
  // Billing: where an admin views subscription, seats, voucher, fiscal profile, tax docs, and cancels.
  '/app/settings/billing',
  // Usage: where an admin reads the organization's seats (assigned, free, members without one).
  '/app/settings/usage',
] as const

/** `/app/settings` itself only redirects to the profile; it stays reachable so that redirect runs. */
const SEAT_FREE_EXACT = new Set(['/app/settings', '/app/settings/'])

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + '/')
}

/** Whether `path` is the chat home, where the chat fills the centre with no page of its own. */
export function isChatHome(path: string): boolean {
  return path === '/app' || path === '/app/'
}

/**
 * The feature whose absence blocks `path` for a caller with these plan flags, or `null` when the page
 * may render. `app` first: a member with no seat (`feature_flags.app` false, which is also what an
 * `unpaid` seat answers) is blocked everywhere in `/app` except the seat-free pages above. Then `chat`
 * on the chat home, then the per-feature table.
 */
export function blockedFeatureFor(path: string, flags: Partial<Record<PlanFeature, boolean>>): PlanFeature | null {
  if (!matchesPrefix(path, '/app')) return null
  const seatFree = SEAT_FREE_EXACT.has(path) || SEAT_FREE_PREFIXES.some(prefix => matchesPrefix(path, prefix))
  if (!seatFree && flags.app !== true) return 'app'
  if (isChatHome(path)) return flags.chat === true ? null : 'chat'
  const feature = gatedFeatureFor(path)
  return feature && flags[feature] !== true ? feature : null
}
